import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import { RootState } from '../store/store';
import { setPId, setParticipant } from '../reducers/participantSlice';

import MemberList from '../components/chat/MemberList';
import JsonMessages from '../components/chat/JsonMessages';

import './ChatRoom.css';

interface ChatRoomProps {
    // Add any props that component needs here
}

interface User {
    id: number;
    name: string;
    messages: { msg: string }[];
    finalMessages: { text: string, timestamp: number }[];
    timer: number | null;
}

const ChatRoom: React.FC<ChatRoomProps> = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const params = useParams();

    const pName = useSelector((state: RootState) => state.participant.name)
    const pId: any = useSelector((state: RootState) => state.participant.pId)
    const [webSocketUrl, setWebSocketUrl] = useState<string | null>(null);

    const timeoutIds = useRef<{ [userId: string]: number | null }>({});
    const chatContainerRef = useRef(null);

    const [users, setUsers] = useState<User[]>([]);
    const [inputValues, setInputValues] = useState<{ [userId: string]: string }>({});
    const [showList, setShowList] = useState<boolean>(false);
    const [isClosed, setIsClosed] = useState(false);
    const [showJson, setShowJson] = useState<boolean>(false);

    const [backspace, setBackspace] = useState<boolean>(false);
    const [jsonMsgHistory, setJsonMsgHistory] = useState<[]>([]);
    const [userList, setUserList] = useState<[]>([]);
    const [selectedPredefinedMessage, setSelectedPredefinedMessage] = useState<string>("");

    const getLocalStorageItem = (key: string) => {
        const item = localStorage.getItem(`rtt-test-${params.pid}`);
        if (!item) return null;
        try {
            const data = JSON.parse(item);
            if (!data[key]) return null;
            return data[key];
        } catch (e) {
            return null;
        }
    }

    const predefinedMessages: Record<string, string> = {
        "hello": "Hello, how may I assist you today?\n",
        "long": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin hendrerit, ante eu semper molestie, nisi nulla gravida augue, eget tristique dolor justo at velit. Pellentesque pulvinar eleifend risus quis interdum. Etiam suscipit et magna vel pellentesque. Phasellus non libero purus. Vivamus id purus at erat posuere lobortis at in quam. Donec ut aliquet augue, at dapibus tellus. Interdum et malesuada fames ac ante ipsum primis in faucibus. Nunc dignissim malesuada sem, eget fermentum dui aliquam a. Nam feugiat interdum odio, vel pharetra purus dapibus ac. Duis ultricies risus leo. Curabitur tincidunt ullamcorper nibh. Nulla euismod mollis nunc sed lacinia.\nMauris scelerisque arcu accumsan, elementum ante at, lobortis felis. Duis quis hendrerit libero. Praesent nec volutpat nibh. Aliquam elementum ac neque at fringilla. Nunc lacinia pretium dictum. Cras auctor nisl tellus, id varius est sodales non. Nullam malesuada viverra placerat. Vivamus et nunc pulvinar, fermentum sem et, venenatis ligula. Cras laoreet diam sed velit sagittis elementum. Mauris sollicitudin arcu a neque auctor mattis. Sed lacinia quam eu convallis fringilla. Phasellus quis aliquet elit, ac sagittis urna.\n",
        "bye": "Thank you for chatting with us today. Have a nice day. Goodbye.\n"
    };

    // Check if pId, PName and webSocketUrl exist, if not, attempt to get them from localStorage
    if (!pId) {
        const localStoragePId = getLocalStorageItem('pId');
        if (localStoragePId) {
            dispatch(setPId({ pId: localStoragePId }));
        }
    }
    if (!pName) {
        const localStoragePName = getLocalStorageItem('pName');
        if (localStoragePName) {
            dispatch(setParticipant({
                name: localStoragePName,
                endpoint: {
                    type: "ws",
                    multiPartyAware: true,
                }
            }));
        }
    }
    if (!webSocketUrl) {
        const localStorageWsUri = getLocalStorageItem('wsUri');
        const localStorageWsToken = getLocalStorageItem('wsToken');
        if (localStorageWsUri && localStorageWsToken) {
            const newWebSocketUrl = `${localStorageWsUri}?access_token=${localStorageWsToken}`;
            setWebSocketUrl(newWebSocketUrl);
        }
    }


    const {
        sendJsonMessage,
        lastJsonMessage,
        readyState,

    } = useWebSocket(webSocketUrl, {
        onOpen: () => {
            //Send a request to join the Conference
            sendJsonMessage(
                {
                    language: "en",
                    type: "JOIN",
                    since: 0,
                    user: {
                        uniqueId: pId,
                        name: pName,
                        role: "PSAP",
                    }
                }
            )
        }, shouldReconnect: (closeEvent) => true,

        onClose: () => {
            setIsClosed(true)
        }

    });

    useEffect(() => {
        // Ensure the chat window automatically scrolls down to show the latest message.
        const chatContainer: any = chatContainerRef.current;
        if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
        }

        // When a new JSON message arrives, update the message history.
        if (lastJsonMessage) {
            setJsonMsgHistory((prev: any) => prev.concat(lastJsonMessage));
            const message = JSON.parse(JSON.stringify(lastJsonMessage));

            // If the message type is a text message, process it.
            if (message.type === "TEXT_MESSAGE") {
                setUsers(prevUsers => {
                    // Find if the user who sent the message already exists in the user list.
                    const userIndex = prevUsers.findIndex(user => user.id === message.user.uniqueId);

                    // If the user is new (not found in the existing list).
                    if (userIndex === -1) {
                        // Finalize the messages of existing users.
                        const updatedUsers = prevUsers.map(user => {
                            if (user.messages.length > 0) {
                                const userMessages = user.messages.map(m => m.msg).join('');
                                return {
                                    ...user,
                                    finalMessages: [...user.finalMessages, { text: userMessages, timestamp: user.timer || Date.now() }],
                                    messages: [],
                                    timer: Date.now()
                                };
                            }
                            return user;
                        });

                        // Add the new user with their first message to the list.
                        const newUser = {
                            id: message.user.uniqueId,
                            name: message.user.name,
                            messages: [{ msg: message.message }],
                            finalMessages: [],
                            timer: message.timestamp
                        };

                        return [...updatedUsers, newUser];
                    } else {
                        // For an existing user, update their message list.
                        const user = prevUsers[userIndex];
                        const currentTime = message.timestamp;
                        const lastMessageTime = user.timer || 0;
                        const timeDifference = currentTime - lastMessageTime;

                        let updatedMessages = [...user.messages];
                        let updatedFinalMessages = [...user.finalMessages];

                        // If more than 5 seconds have passed since the last message, finalize the current message.
                        if (timeDifference > 5000 && updatedMessages.length > 0) {
                            const userMessages = updatedMessages.map(m => m.msg).join('');
                            updatedFinalMessages = [...updatedFinalMessages, { text: userMessages, timestamp: lastMessageTime }];
                            updatedMessages = [];
                        }

                        // If the message is a newline character, finalize the current message and clear the input field.
                        if (message.message === "\n") {
                            const userMessages = updatedMessages.map(m => m.msg).join('');
                            if (userMessages.trim()) {
                                updatedFinalMessages = [...updatedFinalMessages, { text: userMessages, timestamp: currentTime }];
                                updatedMessages = [];
                            }

                            // Clear the input field for this user.
                            setInputValues(prevInputValues => ({
                                ...prevInputValues,
                                [message.user.uniqueId]: ''
                            }));
                        } else if (message.message !== "\b") {
                            // If the message is not a backspace, add it to the current user's messages.
                            updatedMessages = [...updatedMessages, { msg: message.message }];
                        }
                        // Handle the Backspace key by removing the last character or message.
                        if (message.message === "\b" && updatedMessages.length > 0) {
                            const lastMessageIndex = updatedMessages.length - 1;
                            const lastMessage = updatedMessages[lastMessageIndex];

                            // Remove the last message or the last character of the last message.
                            if (lastMessage.msg.length <= 1) {
                                updatedMessages = updatedMessages.slice(0, -1);
                            } else {
                                updatedMessages[lastMessageIndex] = { ...lastMessage, msg: lastMessage.msg.slice(0, -1) };
                            }
                        }

                        // Update the state with the modified list of users.
                        return [...prevUsers.slice(0, userIndex), { ...user, messages: updatedMessages, finalMessages: updatedFinalMessages, timer: currentTime }, ...prevUsers.slice(userIndex + 1)];
                    }
                });
            } else if (message.type === "USER_LIST") {
                // If the message type is a user list, update the user list state.
                setUserList(message.users);
            }
        }
    }, [lastJsonMessage]); // The effect depends on the 'lastJsonMessage' value.

    // This useEffect hook handles the automatic closing of a message bubble after a delay.
    useEffect(() => {
        // Parse the received message. 
        const message = JSON.parse(JSON.stringify(lastJsonMessage));

        // If no message or the message is the "\n" character, return early.
        if (!lastJsonMessage || message.message === "\n") return;

        // If the message type is a text message, process it.
        if (message.type === "TEXT_MESSAGE") {
            const uniqueId = message.user.uniqueId;

            // Clear any existing timer for this user to avoid multiple timers running.
            if (timeoutIds.current[uniqueId] !== null) {
                clearTimeout(timeoutIds.current[uniqueId] as number);
            }

            // Set a new 5-second timer for this user. 
            // After 5 seconds, the following code block will execute.
            timeoutIds.current[uniqueId] = window.setTimeout(() => {
                setUsers(prevUsers => {
                    // Find the user in the current users list.
                    const userIndex = prevUsers.findIndex(u => u.id === uniqueId);
                    if (userIndex === -1) return prevUsers; // If user not found, return the unchanged users array.

                    const updatedUsers = [...prevUsers];
                    // Combine all current messages of the user into a single string.
                    const combinedMessage = updatedUsers[userIndex].messages.map(m => m.msg).join('');

                    // If the combined message is not empty, update the user's final messages.
                    if (combinedMessage.trim() !== "") {
                        updatedUsers[userIndex] = {
                            ...updatedUsers[userIndex],
                            finalMessages: [
                                ...updatedUsers[userIndex].finalMessages,
                                { text: combinedMessage, timestamp: message.timestamp },
                            ],
                            messages: [] // Clear the current messages as they are now finalized.
                        };

                        // Clear the input field for this user.
                        setInputValues(prevInputValues => ({
                            ...prevInputValues,
                            [uniqueId]: ''
                        }));
                    }
                    return updatedUsers;
                });
            }, 5000); // The delay time is set to 5000 milliseconds (5 seconds).
        }
    }, [users]); // This effect depends on the 'users' state.


    const handleSendMessage = (event: any) => {
        //Set input value for each user 
        const newInputValue = event.target.value;
        setInputValues(prevInputValues => ({
            ...prevInputValues,
            [pId]: newInputValue
        }));
        // If the 'backspace' flag is set, Send a JSON message indicating a backspace action 
        if (backspace) {
            sendJsonMessage({ type: "TEXT_MESSAGE", message: String.fromCharCode(8) });
            setBackspace(false)
        }
        // extract the last character from the input field and send it as a TEXT_MESSAGE.
        else {
            const character = event.currentTarget.value[event.currentTarget.value.length - 1];
            sendJsonMessage({ type: "TEXT_MESSAGE", message: character });
        }
    }

    const listHandler = () => {
        if (showJson) setShowJson(!showJson)
        setShowList(!showList)
    }

    const closeListHandler = () => {
        setShowList(!showList)
    }

    const jsonHandler = () => {
        if (showList) setShowList(!showList)
        setShowJson(!showJson)
    }

    const keydownHandler = (event: any) => {
        const inputValue = inputValues[pId];

        // Prevent changing cursor place in input field
        if (event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "ArrowUp") {
            event.preventDefault();
        }

        if (event.keyCode === 8 && inputValue !== '') {
            event.stopPropagation()
            setBackspace(true)
        }

        if (event.keyCode === 13 && inputValue !== '') {
            sendJsonMessage({ type: "TEXT_MESSAGE", message: "\n" })
        }
    }

    function handleMouseDown(event: any) {
        // Prevent text selection in input field
        const input = event.target;
        if (input.value !== '') {
            event.preventDefault();
            // Set the selection start and end to the end of the input value
            const inputLength = input.value.length;
            input.setSelectionRange(inputLength, inputLength);
            // Set focus back to the input field
            input.focus();
        }
    }

    const leaveHandler = () => {
        setIsClosed(true)
        localStorage.removeItem(`rtt-test-${params.pid}`);
        navigate('/')
    }

    useEffect(() => {
        if (!predefinedMessages[selectedPredefinedMessage]) {
            return;
        }

        const message = predefinedMessages[selectedPredefinedMessage].split("");
        setSelectedPredefinedMessage("")
        for (let i = 0; i < message.length; i++) {
            sendJsonMessage({ type: "TEXT_MESSAGE", message: message[i] })
        }
    }, [selectedPredefinedMessage])

    //Map WebSocket 'readyState' values to their human - readable status descriptions.
    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    return (
        <>
            <div className='chatroom-header'>
                <div>
                    <h1> Chat Room </h1 >
                </div>

                <div>
                    <button onClick={jsonHandler} >Json</button>
                    <button onClick={listHandler} className="members">Members</button>
                    <button onClick={leaveHandler} className="leave">Leave</button>
                </div>
            </div>

            <hr />

            <div className="chat-page">

                {/*RTT communication and input field display here */}
                <div className="chat-part">
                    <div className="chat-text" ref={chatContainerRef} >

                        {/* Chat bubbles display here */}
                        <ul>
                            {/* Flatten and sort the final messages from all users */}
                            {users.flatMap(user => user.finalMessages.map(message => ({
                                id: user.id,
                                name: user.name,
                                message
                            })))
                                .sort((a, b) => a.message.timestamp - b.message.timestamp)
                                .map(({ id, name, message }, index) => (
                                    <div key={index}>
                                        <h4 className={pId === id ? "my-name" : "other-name"}>{name}</h4>
                                        {message.text.trim() !== "" && (
                                            <li className={pId === id ? "bubble" : "bubble-red"}>
                                                {message.text}
                                            </li>
                                        )}
                                    </div>
                                ))}
                        </ul >

                        {/* RTT communication during typing display here*/}
                        <ul>
                            {users.map((user, index) =>
                                user.messages.length > 0 && (
                                    <div key={index}>
                                        <h4 className={pId === user.id ? "my-name" : "other-name"}>{user.name} is typing...</h4>
                                        <li className={"bubble-typing"}>
                                            {user.messages.map(m => m.msg)}
                                        </li>
                                    </div>
                                ))}
                        </ul>
                    </div>

                    {/* User name , input field and websocket state display here*/}
                    <div className="sent-message">
                        <p >You are chatting as <strong>{pName}</strong> :</p>
                        <select className="chatroom-predefined-messages" onChange={e => setSelectedPredefinedMessage(e.target.value)} value={selectedPredefinedMessage}>
                            <option value="">Send predefined message...</option>
                            <option value="hello">Hello</option>
                            <option value="long">Lorem ipsum</option>
                            <option value="bye">Bye</option>
                        </select>
                        <input
                            className='chat-input'
                            placeholder="Type your message here..."
                            onChange={handleSendMessage}
                            onKeyDown={keydownHandler}
                            onMouseDown={handleMouseDown}
                            autoComplete="off"
                            value={inputValues[pId] || ''}
                        />
                        <p> WebSocket is currently <strong> {connectionStatus}</strong></p>
                    </div>
                </div>

                {/* Json Messages for each chat display here */}
                {showJson && <JsonMessages jsonMsgHistory={jsonMsgHistory} />}


                {/* List of members in the conference display here */}
                {showList && <MemberList users={userList} onClose={closeListHandler} />}

            </div >
            <hr />
        </>
    );
}

export default ChatRoom;








